import React, { useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Container, Box, Btn, Grid, H1, H4 } from '@saladbob/sassafras'
import { useRouter } from 'next/router';
import { PiArrowLeft } from 'react-icons/pi';

import formatDollars from '../../utils/formatDollars';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import Image from '../media/Image';
import Spinner from '../spinners/Spinner';

import { fetchUser, fetchTeamsNotIn } from '../../hooks/useSwr';
import { useRegistration, RegistrationProvider, useRegistrationDispatch } from '../providers/RegistrationProvider';
import { useUserDispatch } from '../providers/UserProvider';
import { Tournament, Site, Host } from '../../types';

type Props = {
    children: React.ReactNode;
    tournament: Tournament & { host: Host};
    site: Site;
}

dayjs.extend(advancedFormat);

const NavHeader = ({ children }) => (
    <Box bgColor="neutral50" padding="sm">
        <H1 font="leader" weight="normal" align="center" color="neutral" mb="0" ms="auto" me="auto">{children}</H1>
    </Box>
);

const Layout: React.FC<Props> = ({children, tournament }) => {
    const router = useRouter();
    const { user, isLoading: userIsLoading } = fetchUser();
    const { teams, isLoading } = fetchTeamsNotIn(user?.id, tournament.id);
    const registration = useRegistration();
    const userDispatch = useUserDispatch();
    const regDispatch = useRegistrationDispatch();

    const showYear = useMemo(() => {
        const deadlineYear = new Date(tournament.deadline).getFullYear();
        const currentYear = new Date().getFullYear();
        return deadlineYear !== currentYear;
    }, []);

    const sponsors = useMemo(() => {
        return tournament?.host?.sponsors?.reduce((reducer, sponsor) => {
            if (sponsor.teir === 'platinum') {
                reducer.push(sponsor);
            }
            return reducer;
        }, []);
    }, [tournament.host.sponsors]);

    const redirect = (base = '/login') => {
        const { teamId } = router.query;
        const path = router.asPath.split('?')[0];
        const query = teamId
            ? `${path}?step=edit-team&teamId=${teamId}`
            : path;
        const redirect = encodeURIComponent(query);
        router.push(`${base}?redirect=${redirect}`);
    }

    useEffect(() => {
        if (!isLoading && teams) {
            regDispatch({ type: 'replace', data: {teams, isLoading: false }});
        }
    }, [teams, isLoading]);

    useEffect(() => {
        // if (!userIsLoading && user && user.type === 'player' && !user.mackertag) {
        //     redirect('/dashboard/mackertag');
        // } else
        if (!userIsLoading && user) {
            userDispatch({ type: 'replace', user });
        } else if (!userIsLoading && !user) {
            redirect();
        }
    }, [user, userIsLoading]);

    if (!user) {
        return null;
    }

    return (
        <Grid
            columns={{ default: '1fr', laptop: '1fr 4fr' }}
            rows={{ phone: 'min-content 1fr', laptop: '1fr' }}
            minHeight="100vh"
        >
            <Box bgColor="secondary800" depth="md">
                <Box
                    className="hide-on-mobile"
                    bgColor="tertiary"
                    justify="center"
                    align="center"
                    role="button"
                    onClick={() => router.push(`/local/${tournament.host.slug}`)}
                    width="48px"
                    height="48px"
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        zIndex: 2,
                    }}
                >
                    <PiArrowLeft />
                </Box>
                <Box className="hide-on-mobile">
                    <Image
                        alt={tournament.title} image={tournament.image}
                        size="thumbnail"
                        aspectRatio="4/3"
                    />
                </Box>
                <Grid columns={{ phone: '48px 64px 1fr', laptop: '1fr'}} bgColor="tertiary">
                    <Box
                        className="hide-on-laptop"
                        bgColor="tertiary"
                        justify="center"
                        align="center"
                        role="button"
                        onClick={() => router.push(`/local/${tournament.host.slug}`)}
                    >
                        <PiArrowLeft />
                    </Box>
                    <Box className="hide-on-laptop">
                        <Image
                            alt={tournament.title}
                            image={tournament.image}
                            size="thumbnail"
                            width="100%"
                        />
                    </Box>
                    <Box padding="sm">
                        <H4 font="leader" className="hide-on-mobile" mb="0" weight="normal" uppercase>Registering For</H4>
                        <H1 uppercase>{tournament?.title}</H1>
                    </Box>
                </Grid>
                <Box className="hide-on-mobile" mt="md" padding="md">
                    <Btn nowrap color="secondary700" size="lg" font="leader" full mb="lg">
                        Deadline: {dayjs(tournament.deadline).format('MMMM Do')}
                        {showYear && `, ${dayjs(tournament.deadline).format('YYYY')}`}
                    </Btn>
                    <Btn nowrap color="secondary700" size="lg" font="leader" full>
                        Price Per Team: {formatDollars(tournament.price)}
                    </Btn>
                </Box>

                {sponsors?.length > 0 && (
                    <Box display={{ phone: 'none', laptop: 'block' }} padding="sm">
                        <H4 align="center" color="tertiary" mb="md">Brought to You By</H4>
                        {sponsors.map((sponsor) => (
                            <Box maxWidth="240px" margin="auto">
                                <Image
                                    key={sponsor.id}
                                    alt={sponsor.title}
                                    image={sponsor.logo}
                                    size="thumbnail"
                                    pb="100%"
                                    rounded="100%"
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
            <Box overflow="hidden">
                {registration?.heading && (<NavHeader>{registration.heading}</NavHeader>)}
                <Box padding="md">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={isLoading ? 'loading' : 'page'}
                            initial={{ x: -30, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            {isLoading && (
                                <Container size="lg" padding="lg">
                                    <Box minHeight="100vh" verticalAlign="center">  
                                        <Spinner size={120}/>
                                    </Box>
                                </Container>
                            )}
                            {!isLoading && children}
                        </motion.div>
                    </AnimatePresence>
                </Box>
            </Box>
        </Grid>
    )
}

export default function ({children, site, tournament }) {
    return (
        <RegistrationProvider registrations={{ isLoading: true, teams: null }}>
            <Layout site={site} tournament={tournament}>
                {children}
            </Layout>
        </RegistrationProvider>
    );
};